import { Router } from "express";
import { db } from "@workspace/db";
import {
  tendersTable, analysesTable, complianceItemsTable,
  tenderDocumentsTable, companiesTable, subscriptionsTable, plansTable,
} from "@workspace/db";
import { eq, and, desc } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { analyzeCompleteTender } from "../lib/openai";
import { logActivity, logAudit } from "../lib/activity";
import { broadcastTenderUpdate } from "../lib/socket";
import { sendMail, analysisCompleteHtml } from "../lib/mailer";

const router = Router();

router.post("/tenders/:id/analyze", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const { language = "ar", documentId, manualText } = req.body;
    const companyId = req.companyId;

    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db
      .select()
      .from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const company = await db
      .select()
      .from(companiesTable)
      .where(eq(companiesTable.id, companyId))
      .limit(1);

    // Enforce analysis quota from active subscription plan
    const activeSub = await db
      .select({ analysisLimit: plansTable.analysisLimit })
      .from(subscriptionsTable)
      .innerJoin(plansTable, eq(subscriptionsTable.planId, plansTable.id))
      .where(and(eq(subscriptionsTable.companyId, companyId), eq(subscriptionsTable.status, "active")))
      .limit(1);

    const analysisLimit = activeSub[0]?.analysisLimit ?? 5;
    const analysisUsed = company[0]?.analysisUsedThisMonth ?? 0;

    if (analysisUsed >= analysisLimit) {
      return res.status(429).json({
        error: "Analysis limit reached",
        message: `You have used ${analysisUsed}/${analysisLimit} analyses this month. Please upgrade your plan.`,
        used: analysisUsed,
        limit: analysisLimit,
      });
    }

    // Get tender text
    let tenderText = manualText || tender[0].description || "";
    if (documentId) {
      const doc = await db
        .select()
        .from(tenderDocumentsTable)
        .where(and(eq(tenderDocumentsTable.id, documentId), eq(tenderDocumentsTable.tenderId, tenderId)))
        .limit(1);
      if (doc[0]?.extractedText) tenderText = doc[0].extractedText;
    } else if (!tenderText) {
      const latestDoc = await db
        .select()
        .from(tenderDocumentsTable)
        .where(eq(tenderDocumentsTable.tenderId, tenderId))
        .orderBy(desc(tenderDocumentsTable.createdAt))
        .limit(1);
      if (latestDoc[0]?.extractedText) tenderText = latestDoc[0].extractedText;
    }

    if (!tenderText.trim()) {
      return res.status(400).json({ error: "No tender text available for analysis. Please upload a PDF or add a description." });
    }

    // Build company profile string
    const co = company[0];
    const companyProfile = co
      ? `Company: ${co.name}\nSector: ${co.sector || "N/A"}\nExperience: ${co.experience || "N/A"}\nCertifications: ${(co.certifications as string[]).join(", ") || "N/A"}\nTarget Regions: ${(co.targetRegions as string[]).join(", ") || "N/A"}`
      : "No company profile available";

    // Mark as analyzing
    await db
      .update(tendersTable)
      .set({ status: "analyzing", updatedAt: new Date() })
      .where(eq(tendersTable.id, tenderId));

    // AI analysis
    const result = await analyzeCompleteTender(tenderText, companyProfile, language as "ar" | "en");

    // Save analysis
    const analysis = await db
      .insert(analysesTable)
      .values({
        tenderId,
        language,
        executiveSummary: (result.executiveSummary as string) || null,
        tenderType: (result.tenderType as string) || null,
        submissionDeadline: (result.submissionDeadline as string) || null,
        requiredDocuments: (result.requiredDocuments as string[]) || [],
        technicalRequirements: (result.technicalRequirements as string[]) || [],
        financialRequirements: (result.financialRequirements as string[]) || [],
        legalRequirements: (result.legalRequirements as string[]) || [],
        qualificationRequirements: (result.qualificationRequirements as string[]) || [],
        risks: (result.risks as any[]) || [],
        bidDecision: (result.bidDecision as any) || null,
        bidScore: typeof result.bidScore === "number" ? result.bidScore : null,
        bidReasons: (result.bidReasons as string[]) || [],
        missingInformation: (result.missingInformation as string[]) || [],
        proposalOutline: (result.proposalOutline as any) || null,
      })
      .returning();

    // Auto-generate compliance items from requirements
    const allRequirements: Array<{ requirement: string; category: string }> = [
      ...((result.technicalRequirements as string[]) || []).map((r) => ({ requirement: r, category: "technical" as const })),
      ...((result.financialRequirements as string[]) || []).map((r) => ({ requirement: r, category: "financial" as const })),
      ...((result.legalRequirements as string[]) || []).map((r) => ({ requirement: r, category: "legal" as const })),
      ...((result.requiredDocuments as string[]) || []).map((r) => ({ requirement: r, category: "admin" as const })),
    ];

    if (allRequirements.length > 0) {
      await db.insert(complianceItemsTable).values(
        allRequirements.slice(0, 50).map((r) => ({
          tenderId,
          requirement: r.requirement,
          category: r.category as any,
          status: "pending" as const,
        })),
      );
    }

    // Update tender status — calibrate bid decision against company minBidScore threshold
    const rawBidDecision = result.bidDecision as string;
    const bidScore = typeof result.bidScore === "number" ? result.bidScore : null;
    const minBidScore = (company[0] as any)?.minBidScore ?? 60;
    const calibratedBidDecision =
      bidScore != null
        ? bidScore >= minBidScore
          ? "bid"
          : bidScore >= minBidScore - 15
          ? "needs_review"
          : "no_bid"
        : rawBidDecision;
    const bidDecision = calibratedBidDecision;
    const newStatus = bidDecision === "bid" ? "bid" : bidDecision === "no_bid" ? "no_bid" : "ready";
    await db
      .update(tendersTable)
      .set({ status: newStatus as any, hasAnalysis: true, updatedAt: new Date() })
      .where(eq(tendersTable.id, tenderId));

    // Increment usage counter
    await db
      .update(companiesTable)
      .set({ analysisUsedThisMonth: analysisUsed + 1, updatedAt: new Date() })
      .where(eq(companiesTable.id, companyId));

    await logActivity({
      companyId,
      userId: req.dbUser!.id,
      userName: `${req.dbUser!.firstName ?? ""} ${req.dbUser!.lastName ?? ""}`.trim() || req.dbUser!.email,
      action: "analyzed_tender",
      entityType: "tender",
      entityId: tenderId,
      entityTitle: tender[0].title,
    });

    broadcastTenderUpdate(tenderId, "analysis:complete", {
      tenderId,
      bidDecision: analysis[0].bidDecision,
      bidScore: analysis[0].bidScore,
      status: newStatus,
    });

    logAudit({
      userId: req.dbUser!.id,
      userEmail: req.dbUser!.email,
      companyId,
      tenderId,
      action: "create",
      entityType: "analysis",
      entityId: analysis[0].id,
      after: { bidDecision: analysis[0].bidDecision, bidScore: analysis[0].bidScore },
    });

    // Notify the requesting user by email — fire-and-forget
    const isAr = language === "ar";
    sendMail({
      to: req.dbUser!.email,
      subject: isAr
        ? `اكتمل تحليل المناقصة – ${tender[0].title}`
        : `Analysis Complete – ${tender[0].title}`,
      html: analysisCompleteHtml(
        req.dbUser!.firstName ?? "",
        tender[0].title,
        String(analysis[0].bidDecision ?? ""),
        isAr,
      ),
    }).catch(() => {});

    res.json(analysis[0]);
  } catch (err) {
    next(err);
  }
});

/* PATCH – resolve a missing-information item (optionally update tender fields too) */
router.patch("/tenders/:id/analysis/resolve-missing", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId!;
    const { index, resolvedValue, fieldKey } = req.body as {
      index: number;           // index in missingInformation array to remove
      resolvedValue?: string;  // the user-supplied answer/value
      fieldKey?: string;       // optional: "deadline" → also update tender.deadline
    };

    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Not found" });

    const analysis = await db.select().from(analysesTable)
      .where(eq(analysesTable.tenderId, tenderId))
      .orderBy(desc(analysesTable.createdAt)).limit(1);
    if (!analysis[0]) return res.status(404).json({ error: "No analysis" });

    const missing = [...(analysis[0].missingInformation as string[])];
    if (index < 0 || index >= missing.length) return res.status(400).json({ error: "Invalid index" });
    missing.splice(index, 1);

    const updated = await db.update(analysesTable)
      .set({ missingInformation: missing })
      .where(eq(analysesTable.id, analysis[0].id))
      .returning();

    // Optionally update the tender's deadline or description
    if (fieldKey === "deadline" && resolvedValue) {
      const d = new Date(resolvedValue);
      if (!isNaN(d.getTime())) {
        await db.update(tendersTable)
          .set({ deadline: resolvedValue, updatedAt: new Date() })
          .where(eq(tendersTable.id, tenderId));
      }
    }
    if (fieldKey === "submissionDeadline" && resolvedValue) {
      await db.update(analysesTable)
        .set({ submissionDeadline: resolvedValue })
        .where(eq(analysesTable.id, analysis[0].id));
    }

    res.json(updated[0]);
  } catch (err) { next(err); }
});

router.get("/tenders/:id/analysis", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    // Verify caller owns this tender before returning analysis
    const tender = await db
      .select({ id: tendersTable.id })
      .from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const analysis = await db
      .select()
      .from(analysesTable)
      .where(eq(analysesTable.tenderId, tenderId))
      .orderBy(desc(analysesTable.createdAt))
      .limit(1);
    if (!analysis[0]) return res.status(404).json({ error: "No analysis found" });
    res.json(analysis[0]);
  } catch (err) {
    next(err);
  }
});

export default router;
